import { useQueries, useQuery } from '@tanstack/react-query';
import {
  getDynamicForm,
  getQuoteDynamicForm,
  getSubmissionsDynamicForm,
  getSubmissionsDynamicForms,
  getQuoteDynamicFormId,
  getAccountForms,
} from '../../../../api';
import { getData } from '../../../../utils/functional/fp';
import { useAPIErrorHandler } from '../../useAPIErrorHandler';
import type {
  DynamicFormResponse,
  DynamicQuestionSetResponse,
  FormElementSchema,
  FormFieldDef,
} from '../../../../form-engine/types';
import { ElementType } from '../../../../form-engine/parsing/parser';
import type { Nullable } from '../../../../types/common/utility';
import { UNKNOWN_PRODUCT } from '../../../../console/agencies/quotes/PrimeX/constants';
import { ProductTypes, isExcessProduct } from '../../../../types';

export const useDynamicForm = ({ formId, accountId }: UseDynamicFormProps) => {
  const handleAPIError = useAPIErrorHandler();

  return useQuery({
    queryKey: ['dynamic-form', { accountId, formId }],
    queryFn: () =>
      getDynamicForm(accountId!, formId!)
        .then(getData)
        .then((data: any) => ({
          ...data,

          fields: Array.isArray(data.fields)
            ? transformFields(data)
            : data.fields,
        })),
    onError: handleAPIError('Unable to load dynamic form'),
    enabled: !!formId && !!accountId,
  });
};

export const useSubmissionsDynamicFormId = ({
  productType,
  submissionId,
}: UseGetSubmissionsDynamicFormsParams) => {
  const handleAPIError = useAPIErrorHandler();

  return useQuery({
    queryKey: ['dynamic-form-id', { submissionId, productType }],
    queryFn: () =>
      getSubmissionsDynamicForms(submissionId, productType as string)
        .then(getData)
        .then((data: any) => {
          return data?.at(-1)?.id ?? null;
        }),
    onError: handleAPIError('Unable to load dynamic form for submission'),
    enabled: !!submissionId,
  });
};

export const useGetSubmissionDynamicForms = ({
  productType,
  submissionId,
  productLineKeys,
}: UseGetSubmissionsDynamicFormsParams) => {
  const handleAPIError = useAPIErrorHandler();

  const productLineKeysParams = productLineKeys?.map((key) => key).join(',');

  return useQuery({
    queryKey: ['dynamic-forms', { submissionId, productType }],
    queryFn: () =>
      getSubmissionsDynamicForms(submissionId, productType as string, {
        params: {
          ...(productLineKeys && { productLineKeys: productLineKeysParams }),
        },
      })
        .then(getData)
        .then((data) => {
          return data.sort((a, b) => a.ordinal - b.ordinal);
        }),
    onError: handleAPIError('Unable to load dynamic form for submission'),
    cacheTime: 10 * 60 * 1000, // 10 minutes
    enabled:
      !!submissionId &&
      !!productType &&
      productType !== UNKNOWN_PRODUCT &&
      productType !== ProductTypes.notSupported &&
      !isExcessProduct(productType),
  });
};

// effectiveDate is only sent when Edit Quote moves off the saved date, so the
// saved-date request and its cache key stay exactly as they were.
const fetchQuoteDynamicForms = (quoteId: string, effectiveDate?: string) =>
  effectiveDate
    ? getQuoteDynamicFormId(quoteId, { params: { effectiveDate } })
    : getQuoteDynamicFormId(quoteId);

const quoteDynamicFormsListQueryOptions = (
  quoteId: string,
  handleAPIError: ReturnType<typeof useAPIErrorHandler>,
  effectiveDate?: string
) => ({
  queryKey: [
    'quote-dynamic-form-id',
    effectiveDate ? { quoteId, effectiveDate } : { quoteId },
  ] as const,
  queryFn: () =>
    fetchQuoteDynamicForms(quoteId, effectiveDate)
      .then(getData)
      .then((data: DynamicQuestionSetResponse) => {
        const list = Array.isArray(data) ? [...data] : [];
        return list.sort((a, b) => a.ordinal - b.ordinal);
      }),
  onError: handleAPIError('Unable to load dynamic form for submission'),
});

export const useQuoteDynamicForms = ({
  quoteId,
  enabled,
}: UseQuoteDynamicFormIdProps) => {
  const handleAPIError = useAPIErrorHandler();

  return useQuery({
    ...quoteDynamicFormsListQueryOptions(quoteId, handleAPIError),
    enabled: enabled !== undefined ? enabled : !!quoteId,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useAccountForms = ({
  accountId,
  productKey,
  enabled,
  effectiveDate,
  isRenewal = false,
}: UseAccountFormsProps) => {
  const handleAPIError = useAPIErrorHandler();

  return useQuery({
    queryKey: [
      'account-forms',
      { accountId, productKey, effectiveDate, isRenewal },
    ],
    queryFn: () =>
      getAccountForms(accountId, productKey, {
        params: {
          effectiveDate,
          isRenewal,
        },
      })
        .then(getData)
        .then((data) => {
          return data.sort((a, b) => a.ordinal - b.ordinal);
        }),
    onError: handleAPIError('Unable to load dynamic forms for account'),
    enabled: enabled !== undefined ? enabled : !!accountId && !!productKey,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useQuoteDynamicFormId = ({
  quoteId,
  enabled,
  effectiveDate,
}: UseQuoteDynamicFormIdProps) => {
  const handleAPIError = useAPIErrorHandler();

  return useQuery({
    ...quoteDynamicFormsListQueryOptions(
      quoteId,
      handleAPIError,
      effectiveDate
    ),
    select: (sortedForms) => sortedForms?.at(-1)?.id ?? null,
    enabled: enabled !== undefined ? enabled : !!quoteId,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useSubmissionsDynamicForm = ({
  formId,
  submissionId,
  showConfidenceScores = false,
}: UseSubmissionsDynamicFormProps) => {
  const handleAPIError = useAPIErrorHandler();

  return useQuery({
    queryKey: ['dynamic-form', { submissionId, formId }],
    queryFn: () =>
      getSubmissionsDynamicForm(submissionId!, formId!)
        .then(getData)
        .then((data: any) => ({
          ...data,
          fields: Array.isArray(data.fields)
            ? transformFields(data, showConfidenceScores)
            : data.fields,
        })),
    onError: handleAPIError('Unable to load dynamic form for submission'),
    enabled: !!submissionId && !!formId,
  });
};

export const useQuoteDynamicForm = ({
  formId,
  quoteId,
  enabled,
  withDefaults = false,
}: UseQuoteDynamicFormProps) => {
  const handleAPIError = useAPIErrorHandler();

  return useQuery({
    queryKey: ['dynamic-form', { quoteId, formId, withDefaults }],
    queryFn: () =>
      (withDefaults
        ? getQuoteDynamicForm(quoteId!, formId!, {
            params: { withDefaults: true },
          })
        : getQuoteDynamicForm(quoteId!, formId!)
      )
        .then(getData)
        .then((data: any) => ({
          ...data,

          fields: Array.isArray(data.fields)
            ? transformFields(data)
            : data.fields,
        })),
    onError: handleAPIError('Unable to load dynamic form for quote'),
    enabled: enabled ?? (!!formId && !!quoteId),
  });
};

const listToIdMap = <T extends { id: string }>(list: T[]): IdMap<T> => {
  return list.reduce((map, item) => ({ ...map, [item.id]: item }), {});
};

// Only records fieldIds where the raw form element explicitly marks
// required === true. Unmigrated fields serialize required: false (the SDK
// default, not null/absent), so we must ignore false here and let it fall
// through to the JSON-schema `required` check downstream (OR-semantics).
const buildRequiredByFieldId = (
  elements: FormElementSchema[] | undefined,
  acc: Record<string, boolean> = {}
): Record<string, boolean> => {
  if (!Array.isArray(elements)) {
    return acc;
  }

  for (const element of elements) {
    if (element.type === ElementType.Section) {
      buildRequiredByFieldId(element.elements, acc);
    } else if (
      element.type === ElementType.Field &&
      element.required === true
    ) {
      acc[element.fieldId] = true;
    }
  }

  return acc;
};

const applyRequiredOverrides = (
  fieldsById: IdMap<FormFieldDef>,
  requiredByFieldId: Record<string, boolean>
): IdMap<FormFieldDef> => {
  return Object.keys(fieldsById).reduce<IdMap<FormFieldDef>>((acc, fieldId) => {
    const field = fieldsById[fieldId];
    if (requiredByFieldId[fieldId] === true) {
      return { ...acc, [fieldId]: { ...field, required: true } };
    }
    return { ...acc, [fieldId]: field };
  }, {});
};

export const transformFields = (
  data: DynamicFormResponse,
  showConfidenceScores?: boolean
) => {
  const requiredByFieldId = buildRequiredByFieldId(data.elements);

  const merged = data.fieldOverrides
    ? mergeFieldsWithFieldOverrides(
        data.fields,
        data.fieldOverrides,
        showConfidenceScores ? data.confidenceScores : {}
      )
    : listToIdMap(data.fields);

  return applyRequiredOverrides(merged, requiredByFieldId);
};

const mergeFieldsWithFieldOverrides = (
  fields: DynamicFormResponse['fields'],
  fieldOverrides: DynamicFormResponse['fieldOverrides'],
  confidenceScores?: DynamicFormResponse['confidenceScores']
) => {
  const fieldsIdMap = listToIdMap(fields);
  const fieldOverridesIdMap = listToIdMap(fieldOverrides);

  return Object.keys(fieldsIdMap).reduce<IdMap<FormFieldDef>>(
    (mergedFields, fieldId) => {
      const newFieldDef: any = {
        ...fieldsIdMap[fieldId],
        ...(confidenceScores?.[fieldId]
          ? { confidenceScore: confidenceScores?.[fieldId] }
          : {}),
      };

      const overridesForField = fieldOverridesIdMap[fieldId];
      if (overridesForField) {
        Object.entries(overridesForField).forEach(([property, overrideVal]) => {
          if (
            property === 'valueOptions' &&
            Array.isArray(overrideVal) &&
            overrideVal.length == 0
          ) {
            return;
          }
          newFieldDef[property as keyof FormFieldDef] = overrideVal;
        });
      }

      return { ...mergedFields, [fieldId]: newFieldDef };
    },
    {}
  );
};

type UseAccountFormsProps = {
  accountId: string;
  productKey: string;
  enabled?: boolean;
  effectiveDate?: string;
  isRenewal: boolean;
};
type UseDynamicFormProps = {
  formId: Nullable<string>;
  accountId: Nullable<string>;
};
type UseSubmissionsDynamicFormProps = {
  formId: Nullable<string>;
  submissionId: Nullable<string>;
  options?: Record<string, unknown>;
  showConfidenceScores?: boolean;
};
type UseQuoteDynamicFormProps = {
  formId: Nullable<string>;
  quoteId: Nullable<string>;
  enabled?: boolean;
  /** When true, GET quote form includes mothership defaults for unanswered fields (fir-account). */
  withDefaults?: boolean;
};
type IdMap<T = unknown> = {
  [key: string]: T;
};
type UseGetSubmissionsDynamicFormsParams = {
  submissionId: string;
  productType: string;
  productLineKeys?: string[];
  options?: Record<string, unknown>;
};

type UseQuoteDynamicFormIdProps = {
  quoteId: string;
  options?: Record<string, unknown>;
  enabled?: boolean;
  /** Resolve forms for this date instead of the saved quote's (YYYY-MM-DD). */
  effectiveDate?: string;
};

export const useSubmissionForms = (
  submissionId: Nullable<string>,
  formIds: Nullable<string[]>
) => {
  return useQueries({
    queries: (formIds || []).map((formId) => ({
      queryKey: ['submission-forms', submissionId, formId],
      queryFn: () =>
        getSubmissionsDynamicForm(submissionId!, formId).then(getData),
    })),
  });
};
